/**
 * TEMP-H-003: Timeline Filters Component
 * 
 * Advanced filtering controls for execution timeline events.
 * Supports stage, event type, persona, and time range filtering.
 */

import React, { useState, useMemo } from 'react';
import { 
  Filter, 
  X, 
  Clock, 
  Tag, 
  User, 
  Activity,
  ChevronDown
} from 'lucide-react';
import type { 
  TimelineFilters,
  EventTypeCategory
} from '@/types/execution-events';
import {
  EVENT_TYPE_CATEGORIES,
  STAGE_DISPLAY_CONFIG,
  EVENT_TYPE_DISPLAY_CONFIG
} from '@/types/execution-events';
import type { StageType } from '@/types/execution';
import type { EventType } from '@/types/events-v2';

interface TimelineFiltersProps {
  filters: TimelineFilters;
  onFiltersChange: (filters: TimelineFilters) => void;
  onReset: () => void;
  className?: string;
}


export function TimelineFilters({ 
  filters, 
  onFiltersChange, 
  onReset,
  className = ''
}: TimelineFiltersProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['stages', 'eventTypes'])
  );

  // Available options
  const stageOptions = useMemo(() => {
    return Object.entries(STAGE_DISPLAY_CONFIG)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([stage, config]) => ({
        value: stage as StageType,
        label: config.label,
        icon: config.icon,
        color: config.color
      }));
  }, []);

  const eventTypeOptions = useMemo(() => {
    return Object.entries(EVENT_TYPE_DISPLAY_CONFIG).map(([type, config]) => ({
      value: type as EventType,
      label: config.label,
      icon: config.icon,
      category: config.category,
      severity: config.severity
    }));
  }, []);

  const eventTypesByCategory = useMemo(() => {
    const grouped = new Map<EventTypeCategory, typeof eventTypeOptions>();
    
    Object.entries(EVENT_TYPE_CATEGORIES).forEach(([category, types]) => {
      const categoryTypes = eventTypeOptions.filter(option => 
        (types as readonly string[]).includes(option.value)
      );
      grouped.set(category as EventTypeCategory, categoryTypes);
    });
    
    return grouped;
  }, [eventTypeOptions]);

  // Active filter counts
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.stage?.length) count += filters.stage.length;
    if (filters.eventType?.length) count += filters.eventType.length;
    if (filters.persona?.length) count += filters.persona.length;
    if (filters.timeRange) count += 1;
    return count;
  }, [filters]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const updateStageFilter = (stage: StageType, checked: boolean) => {
    const current = filters.stage || [];
    const updated = checked 
      ? [...current, stage]
      : current.filter(s => s !== stage);
    
    onFiltersChange({
      ...filters,
      stage: updated.length > 0 ? updated : undefined
    });
  };

  const updateEventTypeFilter = (eventType: EventType, checked: boolean) => {
    const current = filters.eventType || [];
    const updated = checked
      ? [...current, eventType]
      : current.filter(t => t !== eventType);
    
    onFiltersChange({
      ...filters,
      eventType: updated.length > 0 ? updated : undefined
    });
  };

  const updateTimeRange = (field: 'from' | 'to', value: string) => {
    const current = filters.timeRange || { from: '', to: '' };
    const updated = { ...current, [field]: value };
    
    // Only set timeRange if at least one field has a value
    if (updated.from || updated.to) {
      onFiltersChange({
        ...filters,
        timeRange: updated
      });
    } else {
      onFiltersChange({
        ...filters,
        timeRange: undefined
      });
    }
  };

  const FilterSection = ({ 
    id, 
    title, 
    icon, 
    children 
  }: { 
    id: string; 
    title: string; 
    icon: React.ReactNode; 
    children: React.ReactNode;
  }) => {
    const isExpanded = expandedSections.has(id);
    
    return (
      <div className="border border-white/10 rounded-lg">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between p-3 text-left hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-medium text-white">{title}</span>
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`} 
          />
        </button>
        
        {isExpanded && (
          <div className="border-t border-white/10 p-3">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-medium text-white">Timeline Filters</h3>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-300 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        
        {activeFilterCount > 0 && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-3 h-3" />
            Clear All
          </button>
        )}
      </div>

      {/* Stage Filter */}
      <FilterSection
        id="stages"
        title="Stages"
        icon={<Activity className="w-4 h-4 text-blue-400" />}
      >
        <div className="grid grid-cols-2 gap-2">
          {stageOptions.map(option => (
            <label
              key={option.value}
              className="flex items-center gap-2 p-2 rounded-md hover:bg-white/5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={filters.stage?.includes(option.value) || false}
                onChange={(e) => updateStageFilter(option.value, e.target.checked)}
                className="w-3 h-3 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm">{option.icon}</span>
              <span className="text-xs text-gray-300 flex-1">{option.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Event Type Filter */}
      <FilterSection
        id="eventTypes"
        title="Event Types"
        icon={<Tag className="w-4 h-4 text-green-400" />}
      >
        <div className="space-y-3">
          {Array.from(eventTypesByCategory.entries()).map(([category, types]) => (
            <div key={category}>
              <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                {category.replace(/([A-Z])/g, ' $1').trim()}
              </h4>
              <div className="space-y-1">
                {types.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-white/5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filters.eventType?.includes(option.value) || false}
                      onChange={(e) => updateEventTypeFilter(option.value, e.target.checked)}
                      className="w-3 h-3 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500/20"
                    />
                    <span className="text-xs">{option.icon}</span>
                    <span className="text-xs text-gray-300 flex-1">{option.label}</span>
                    <span className={`w-2 h-2 rounded-full ${
                      option.severity === 'success' ? 'bg-green-500' :
                      option.severity === 'error' ? 'bg-red-500' :
                      option.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`} />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </FilterSection>

      {/* Time Range Filter */}
      <FilterSection
        id="timeRange"
        title="Time Range"
        icon={<Clock className="w-4 h-4 text-orange-400" />}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">From</label>
            <input
              type="datetime-local"
              value={filters.timeRange?.from ? 
                new Date(filters.timeRange.from).toISOString().slice(0, 16) : ''
              }
              onChange={(e) => updateTimeRange('from', 
                e.target.value ? new Date(e.target.value).toISOString() : ''
              )}
              className="w-full px-2 py-1.5 text-xs bg-gray-800 border border-gray-600 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1">To</label>
            <input
              type="datetime-local"
              value={filters.timeRange?.to ? 
                new Date(filters.timeRange.to).toISOString().slice(0, 16) : ''
              }
              onChange={(e) => updateTimeRange('to',
                e.target.value ? new Date(e.target.value).toISOString() : ''
              )}
              className="w-full px-2 py-1.5 text-xs bg-gray-800 border border-gray-600 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
          </div>

          {/* Quick time range buttons */}
          <div className="flex flex-wrap gap-1">
            {[
              { label: 'Last Hour', hours: 1 },
              { label: 'Last 6h', hours: 6 },
              { label: 'Last 24h', hours: 24 },
              { label: 'Last 7d', hours: 24 * 7 }
            ].map(({ label, hours }) => (
              <button
                key={label}
                onClick={() => {
                  const now = new Date();
                  const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
                  onFiltersChange({
                    ...filters,
                    timeRange: {
                      from: from.toISOString(),
                      to: now.toISOString()
                    }
                  });
                }}
                className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </FilterSection>

      {/* Persona Filter (if we have persona data) */}
      {filters.persona && filters.persona.length > 0 && (
        <FilterSection
          id="personas"
          title="QC Personas"
          icon={<User className="w-4 h-4 text-teal-400" />}
        >
          <div className="space-y-1">
            {filters.persona.map(persona => (
              <div key={persona} className="text-xs text-gray-300">
                {persona}
              </div>
            ))}
          </div>
        </FilterSection>
      )}
    </div>
  );
}